import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import type { Task, User, Establishment, CreateTaskRequest, TaskStatus } from '../types';

export default function TaskList() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [establishments, setEstablishments] = useState<Establishment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Filters
  const statusFilter = searchParams.get('status') as TaskStatus | null;
  const userFilter = searchParams.get('user_id') ? parseInt(searchParams.get('user_id')!) : undefined;
  const establishmentFilter = searchParams.get('establishment_id') ? parseInt(searchParams.get('establishment_id')!) : undefined;

  useEffect(() => {
    loadFilters();
  }, [user]);

  useEffect(() => {
    loadTasks();
  }, [user, statusFilter, userFilter, establishmentFilter]);

  const loadFilters = async () => {
    if (!user) return;
    try {
      const [usersRes, estRes] = await Promise.all([
        api.getUsers(),
        api.getEstablishments(),
      ]);

      // Filter users list based on role
      let filteredUsers = usersRes;
      if (user.role === 'manager') {
        // Managers see their employees
        filteredUsers = usersRes.filter(u => u.manager_id === user.id);
      }

      setUsers(filteredUsers);
      setEstablishments(estRes);
    } catch (err) {
      console.error('Failed to load filters:', err);
    }
  };

  const loadTasks = async () => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);

      // Build filters based on user role
      let taskFilters: {
        user_id?: number;
        manager_id?: number;
        establishment_id?: number;
        status?: TaskStatus;
        limit: number;
      } = {
        status: statusFilter || undefined,
        limit: 50,
      };

      if (user.role === 'employee') {
        // Employee sees only their tasks
        taskFilters.user_id = user.id;
      } else if (user.role === 'manager') {
        // Manager sees their employees' tasks
        if (userFilter) {
          // Filter by specific employee
          taskFilters.user_id = userFilter;
        } else {
          taskFilters.manager_id = user.id;
        }
      } else if (user.role === 'admin') {
        // Admin sees all tasks with optional filters
        if (userFilter) {
          taskFilters.user_id = userFilter;
        }
        if (establishmentFilter) {
          taskFilters.establishment_id = establishmentFilter;
        }
      }

      const response = await api.getTasks(taskFilters);

      setTasks(response.tasks);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusFilter = (status: string | null) => {
    if (status) {
      searchParams.set('status', status);
    } else {
      searchParams.delete('status');
    }
    setSearchParams(searchParams);
  };

  const handleUserFilter = (userId: string) => {
    if (userId) {
      searchParams.set('user_id', userId);
    } else {
      searchParams.delete('user_id');
    }
    setSearchParams(searchParams);
  };

  const handleEstablishmentFilter = (estId: string) => {
    if (estId) {
      searchParams.set('establishment_id', estId);
    } else {
      searchParams.delete('establishment_id');
    }
    setSearchParams(searchParams);
  };

  const clearFilters = () => {
    searchParams.delete('status');
    searchParams.delete('user_id');
    searchParams.delete('establishment_id');
    setSearchParams(searchParams);
  };

  const hasActiveFilters = statusFilter || userFilter || establishmentFilter;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h2>Задачи</h2>
        {user?.role !== 'employee' && (
          <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
            + Создать задачу
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="tabs">
        <button
          className={`tab ${!statusFilter ? 'active' : ''}`}
          onClick={() => handleStatusFilter(null)}
        >
          Все
        </button>
        <button
          className={`tab ${statusFilter === 'pending' ? 'active' : ''}`}
          onClick={() => handleStatusFilter('pending')}
        >
          Ожидают
        </button>
        <button
          className={`tab ${statusFilter === 'in_progress' ? 'active' : ''}`}
          onClick={() => handleStatusFilter('in_progress')}
        >
          В работе
        </button>
        <button
          className={`tab ${statusFilter === 'completed' ? 'active' : ''}`}
          onClick={() => handleStatusFilter('completed')}
        >
          Выполнено
        </button>
      </div>

      {/* Additional filters for manager/admin */}
      {(user?.role === 'manager' || user?.role === 'admin') && (
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
          <select
            className="form-select"
            style={{ width: 'auto', minWidth: '150px' }}
            value={userFilter || ''}
            onChange={(e) => handleUserFilter(e.target.value)}
          >
            <option value="">Все сотрудники</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.first_name} {u.last_name || ''}
              </option>
            ))}
          </select>

          {user?.role === 'admin' && (
            <select
              className="form-select"
              style={{ width: 'auto', minWidth: '150px' }}
              value={establishmentFilter || ''}
              onChange={(e) => handleEstablishmentFilter(e.target.value)}
            >
              <option value="">Все заведения</option>
              {establishments.map((est) => (
                <option key={est.id} value={est.id}>
                  {est.name}
                </option>
              ))}
            </select>
          )}

          {hasActiveFilters && (
            <button
              className="btn btn-secondary"
              style={{ padding: '0.5rem 1rem' }}
              onClick={clearFilters}
            >
              Очистить
            </button>
          )}
        </div>
      )}

      {loading ? (
        <div className="loading">
          <div className="spinner"></div>
        </div>
      ) : error ? (
        <div className="error-message">{error}</div>
      ) : tasks.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">📋</div>
          <p>Нет задач</p>
        </div>
      ) : (
        <div className="task-list">
          {tasks.map((task) => (
            <TaskCard key={task.id} task={task} currentUser={user} onDelete={loadTasks} />
          ))}
        </div>
      )}

      {showCreateModal && (
        <CreateTaskModal
          onClose={() => setShowCreateModal(false)}
          onCreated={loadTasks}
          currentUser={user}
        />
      )}
    </div>
  );
}

function TaskCard({ task, currentUser, onDelete }: { task: Task; currentUser: User | null; onDelete: () => void }) {
  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const isOverdue = task.due_date && new Date(task.due_date) < new Date() &&
    !['completed', 'cancelled'].includes(task.status);

  const canDelete = currentUser?.role === 'admin' ||
    (currentUser?.role === 'manager' && task.creator_user_id === currentUser.id);

  const handleDelete = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!confirm('Удалить задачу?')) return;
    try {
      await api.deleteTask(task.id);
      onDelete();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete task');
    }
  };

  return (
    <Link to={`/tasks/${task.id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
      <div className="task-item">
        <div className="task-header">
          <span className="task-title">{task.title}</span>
          <span className={`task-status status-${task.status}`}>
            {getStatusText(task.status)}
          </span>
        </div>
        <div className="task-meta">
          {task.due_date && (
            <span className="task-meta-item" style={{ color: isOverdue ? 'var(--danger)' : undefined }}>
              📅 {formatDate(task.due_date)}
            </span>
          )}
          {task.establishment_name && (
            <span className="task-meta-item">🏢 {task.establishment_name}</span>
          )}
          {task.assigned_first_name && (
            <span className="task-meta-item">👤 {task.assigned_first_name}</span>
          )}
          {canDelete && (
            <button
              className="btn btn-danger"
              style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem', marginLeft: '0.5rem' }}
              onClick={handleDelete}
            >
              Удалить
            </button>
          )}
        </div>
      </div>
    </Link>
  );
}

function getStatusText(status: string): string {
  const map: Record<string, string> = {
    pending: 'Ожидает',
    in_progress: 'В работе',
    questions: 'Вопросы',
    completed: 'Выполнено',
    cancelled: 'Отменено',
  };
  return map[status] || status;
}

function CreateTaskModal({ onClose, onCreated, currentUser }: { onClose: () => void; onCreated: () => void; currentUser: User | null }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [assignees, setAssignees] = useState<number[]>([]);
  const [establishmentId, setEstablishmentId] = useState<number | undefined>();
  const [dueDate, setDueDate] = useState('');
  const [reminderRule, setReminderRule] = useState('');
  const [priority, setPriority] = useState(0);
  
  // Repeat options
  const [repeatType, setRepeatType] = useState<'none' | 'daily' | 'weekly' | 'monthly' | 'specificDates'>('none');
  const [repeatDays, setRepeatDays] = useState<number[]>([]);
  const [repeatDates, setRepeatDates] = useState<string[]>([]);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [users, setUsers] = useState<User[]>([]);
  const [establishments, setEstablishments] = useState<Establishment[]>([]);

  useEffect(() => {
    loadData();
  }, [currentUser]);

  const loadData = async () => {
    try {
      const [usersRes, estRes] = await Promise.all([
        api.getUsers(),
        api.getEstablishments(),
      ]);

      // Filter users: managers see only their employees, admins see all
      let filteredUsers = usersRes;
      if (currentUser?.role === 'manager') {
        filteredUsers = usersRes.filter(u => u.manager_id === currentUser.id || u.id === currentUser.id);
      }

      setUsers(filteredUsers);
      setEstablishments(estRes);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    }
  };

  const getRepeatRuleJson = (): string | undefined => {
    if (repeatType === 'none') return undefined;
    
    const rule: Record<string, unknown> = { type: repeatType };
    
    if (repeatType === 'weekly' && repeatDays.length > 0) {
      rule.days = repeatDays;
    } else if (repeatType === 'monthly' && repeatDays.length > 0) {
      rule.days = repeatDays;
    } else if (repeatType === 'specificDates' && repeatDates.length > 0) {
      rule.dates = repeatDates;
    }
    
    return JSON.stringify(rule);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title || assignees.length === 0) return;

    setLoading(true);
    setError(null);

    try {
      const taskData: CreateTaskRequest = {
        title,
        description: description || undefined,
        assigned_user_ids: assignees,
        establishment_id: establishmentId,
        due_date: dueDate || undefined,
        reminder_rule: reminderRule || undefined,
        priority,
        repeat_rule_json: getRepeatRuleJson(),
      };

      await api.createTask(taskData);
      onCreated();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create task');
    } finally {
      setLoading(false);
    }
  };

  const toggleAssignee = (userId: number) => {
    setAssignees((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const toggleRepeatDay = (day: number) => {
    setRepeatDays(prev => 
      prev.includes(day) 
        ? prev.filter(d => d !== day)
        : [...prev, day]
    );
  };

  const addRepeatDate = () => {
    setRepeatDates(prev => [...prev, '']);
  };

  const updateRepeatDate = (index: number, value: string) => {
    const newDates = [...repeatDates];
    newDates[index] = value;
    setRepeatDates(newDates);
  };

  const removeRepeatDate = (index: number) => {
    setRepeatDates(prev => prev.filter((_, i) => i !== index));
  };

  const weekDays = [
    { value: 0, label: 'Вс' },
    { value: 1, label: 'Пн' },
    { value: 2, label: 'Вт' },
    { value: 3, label: 'Ср' },
    { value: 4, label: 'Чт' },
    { value: 5, label: 'Пт' },
    { value: 6, label: 'Сб' },
  ];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title">Создать задачу</h3>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Название *</label>
            <input
              type="text"
              className="form-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Описание</label>
            <textarea
              className="form-textarea"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Исполнители *</label>
            <div className="checkbox-group">
              {users.map((u) => (
                <label key={u.id} className="checkbox-item">
                  <input
                    type="checkbox"
                    className="checkbox-input"
                    checked={assignees.includes(u.id)}
                    onChange={() => toggleAssignee(u.id)}
                  />
                  <span>{u.first_name} {u.last_name || ''}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Заведение</label>
            <select
              className="form-select"
              value={establishmentId || ''}
              onChange={(e) => setEstablishmentId(e.target.value ? parseInt(e.target.value) : undefined)}
            >
              <option value="">Не выбрано</option>
              {establishments.map((est) => (
                <option key={est.id} value={est.id}>{est.name}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Срок</label>
            <input
              type="datetime-local"
              className="form-input"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Напоминание</label>
            <select
              className="form-select"
              value={reminderRule}
              onChange={(e) => setReminderRule(e.target.value)}
            >
              <option value="">Без напоминания</option>
              <option value="30m">За 30 минут</option>
              <option value="1h">За 1 час</option>
              <option value="2h">За 2 часа</option>
              <option value="24h">За 24 часа</option>
              <option value="1d">За 1 день</option>
              <option value="2d">За 2 дня</option>
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Приоритет</label>
            <select
              className="form-select"
              value={priority}
              onChange={(e) => setPriority(parseInt(e.target.value))}
            >
              <option value="0">Низкий</option>
              <option value="1">Средний</option>
              <option value="2">Высокий</option>
              <option value="3">Срочный</option>
            </select>
          </div>

          {/* Repeat Options */}
          <div className="form-group">
            <label className="form-label">Повтор</label>
            <select
              className="form-select"
              value={repeatType}
              onChange={(e) => setRepeatType(e.target.value as typeof repeatType)}
            >
              <option value="none">Не повторять</option>
              <option value="daily">Каждый день</option>
              <option value="weekly">Каждую неделю</option>
              <option value="monthly">Каждый месяц</option>
              <option value="specificDates">В выбранные даты</option>
            </select>
          </div>

          {repeatType === 'weekly' && (
            <div className="form-group">
              <label className="form-label">Дни недели</label>
              <div className="checkbox-group" style={{ flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'space-between' }}>
                {weekDays.map((day) => (
                  <label key={day.value} className="checkbox-item" style={{ padding: '0.25rem' }}>
                    <input
                      type="checkbox"
                      className="checkbox-input"
                      checked={repeatDays.includes(day.value)}
                      onChange={() => toggleRepeatDay(day.value)}
                    />
                    <span style={{ fontSize: '0.8rem' }}>{day.label}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {repeatType === 'monthly' && (
            <div className="form-group">
              <label className="form-label">Дни месяца (1-28)</label>
              <div className="checkbox-group" style={{ flexDirection: 'row', flexWrap: 'wrap', maxHeight: '100px' }}>
                {Array.from({ length: 28 }, (_, i) => i + 1).map((day) => (
                  <label key={day} className="checkbox-item" style={{ padding: '0.2rem' }}>
                    <input
                      type="checkbox"
                      className="checkbox-input"
                      checked={repeatDays.includes(day)}
                      onChange={() => toggleRepeatDay(day)}
                    />
                    <span style={{ fontSize: '0.75rem' }}>{day}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {repeatType === 'specificDates' && (
            <div className="form-group">
              <label className="form-label">Даты</label>
              {repeatDates.map((date, index) => (
                <div key={index} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <input
                    type="date"
                    className="form-input"
                    value={date}
                    onChange={(e) => updateRepeatDate(index, e.target.value)}
                  />
                  <button
                    type="button"
                    className="btn btn-secondary"
                    style={{ padding: '0.5rem' }}
                    onClick={() => removeRepeatDate(index)}
                  >
                    X
                  </button>
                </div>
              ))}
              <button
                type="button"
                className="btn btn-secondary"
                onClick={addRepeatDate}
                style={{ width: '100%' }}
              >
                + Добавить дату
              </button>
            </div>
          )}

          <button type="submit" className="btn btn-primary btn-full" disabled={loading || !title || assignees.length === 0}>
            {loading ? 'Создание...' : 'Создать задачу'}
          </button>
        </form>
      </div>
    </div>
  );
}
